/**
 * Relative-time formatter — neutral presentation capability.
 *
 * Figma authority: R4VYIdXEv7HYIXtpDMEvHY, node 38:2510
 *
 * Pure function: caller supplies the reference time.
 * No Date.now() read inside the formatter.
 *
 * Russian output contract (per CORRECTION01):
 *   < 1 minute       → только что
 *   1–59 minutes     → N мин. назад
 *   1 hour           → 1 час назад
 *   2, 3 or 4 hours  → N часа назад
 *   other hours      → N часов назад
 *
 * Fail-closed behavior:
 *   - invalid timestamp (NaN) → '—'  (em-dash, never 'NaN мин. назад')
 *   - future timestamp (timestamp > referenceTime) → 'только что'
 *     This is fail-closed because a future timestamp indicates the device
 *     clock is wrong, and silently rendering "N мин. назад" would be a lie.
 *
 * Lives outside any fixture adapter so production code can import it
 * without dragging in fixture data.
 */

/**
 * Format an ISO 8601 timestamp as a Russian relative-time string.
 *
 * Pure: no Date.now(), no module-level state.
 *
 * @param isoTimestamp  ISO 8601 timestamp string.
 * @param referenceTime Date instance defining "now" for the comparison.
 * @returns             Russian relative-time copy.
 */
export function formatRelativeTime(
  isoTimestamp: string,
  referenceTime: Date
): string {
  if (!isFiniteNumber(referenceTime.getTime())) {
    return '—';
  }

  const parsed = new Date(isoTimestamp);
  const timestampMs = parsed.getTime();

  if (!isFiniteNumber(timestampMs)) {
    // Fail-closed: do not leak NaN into visible copy.
    return '—';
  }

  const referenceMs = referenceTime.getTime();
  const diffMs = referenceMs - timestampMs;

  // Future timestamps: clock skew, or caller-provided reference before event.
  // Fail-closed: render as "только что" rather than fabricate a negative age.
  if (diffMs < 0) {
    return 'только что';
  }

  const minutes = Math.floor(diffMs / 60_000);
  const hours = Math.floor(diffMs / 3_600_000);

  if (minutes < 1) {
    return 'только что';
  }
  if (minutes < 60) {
    return `${minutes} мин. назад`;
  }
  if (hours === 1) {
    return '1 час назад';
  }
  if (hours === 2 || hours === 3 || hours === 4) {
    return `${hours} часа назад`;
  }
  return `${hours} часов назад`;
}

function isFiniteNumber(value: number): boolean {
  return typeof value === 'number' && Number.isFinite(value);
}